import cn from "classnames";
import { AccessPermitsList } from "components/shared/accessPermitsList";
import BookIndex from "components/shared/book/list";
import BookItemStatus from "components/shared/book/statusCell";
import DropdownBtn from "components/shared/dropdown";
import InlineBtn from "components/shared/inlineBtn";
import SharedReactTable from "components/shared/reactTable";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { dateTimeToView } from "helpers/date";
import { accessPermitRelationTypesEnum, featureModuleTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import {
    BooleanColumnFilter,
    DateColumnFilter,
    EnumColumnFilter,
    isDeletedColumn,
    NumericColumnFilter,
    rowIndexColumn,
    SelectColumnFilter,
} from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { enumLabel } from "helpers/system";
import policy from "policies";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { selectList as accessPermitsList, getInfoList } from "sources/books/accessPermits";
import { list as logistsList } from "sources/books/employees";
import { list as mechanicsList } from "sources/books/mechanics";
import { list as transportColumnsList } from "sources/books/transportColumns";
import {
    addAllCarsToRefuelingSyncList,
    addCarsToRefuelingSyncList,
    removeAllCarsFromRefuelingSyncList,
    removeCarsFromRefuelingSyncList,
    setResourceActivity,
} from "sources/books/trucks";
import { list as columnDispatchersList } from "sources/columnDispatcher";

class TrucksList extends BookIndex {
    state = { selectedItems: [], tableColumns: this.tableColumns, accessLoaded: false, accessPermits: [] };

    get tableColumns() {
        const checkboxColumn = {
            Header: () => {
                const allItemsSelected = this.state.selectedItems.length === this.props.items.length;
                return (
                    <div className="d-flex justify-content-center pt-1">
                        <span
                            className={cn("checkbox", { "checkbox--checked": allItemsSelected })}
                            onClick={() =>
                                allItemsSelected
                                    ? this.setState({ selectedItems: [] })
                                    : this.setState({ selectedItems: this.props.items })
                            }
                        />
                    </div>
                );
            },
            accessor: "checkbox",
            width: 30,
            sortable: false,
            Cell: ({ row: { original } }) => {
                const itemSelected = this.state.selectedItems.some((item) => item.id === original.id);
                return (
                    <div className="d-flex justify-content-center pt-1">
                        <span
                            className={cn("checkbox", { "checkbox--checked": itemSelected })}
                            onClick={() =>
                                itemSelected
                                    ? this.setState({
                                          selectedItems: this.state.selectedItems.filter(
                                              (item) => item.id !== original.id,
                                          ),
                                      })
                                    : this.setState({ selectedItems: this.state.selectedItems.concat(original) })
                            }
                        />
                    </div>
                );
            },
        };
        const refuelingInfoColumns = [
            {
                Header: "Объем бака",
                id: "carFuelTank/totalVolume",
                accessor: (d) => d.carFuelTank?.totalVolume,
                filterable: true,
                filterType: "numeric",
                Filter: NumericColumnFilter,
            },
            {
                Header: "Расход топлива л/100 км.",
                id: "carFuelTank/fuelConsumption",
                accessor: (d) => d.carFuelTank?.fuelConsumption,
                filterable: true,
                filterType: "numeric",
                Filter: NumericColumnFilter,
            },
            {
                Header: "Последнее обновление ДУТ",
                accessor: "lastDieselSensorValueFixedAt",
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
                CSVExport: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Участвует в планировании АЗС",
                accessor: "isInRefuelingSyncList",
                sortable: false,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => {
                    const options = [
                        { label: "Показать все", value: "all" },
                        { label: "Да", value: "true" },
                        { label: "Нет", value: "false" },
                    ];
                    return <BooleanColumnFilter {...props} options={options} />;
                },
                Cell: ({ value }) => <div className="text-center">{value ? "Да" : "Нет"}</div>,
                CSVExport: ({ value }) => (value ? "Да" : "Нет"),
            },
        ];
        const { isTruckingOrganizationAdmin } = this.props.roles;
        const { availableFeatureModules } = this.props.profile;
        const { refuelingSettings } = this.props;
        const refuelingEnabled =
            availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) &&
            !refuelingSettings.hideRefuelingPlanning;
        const syncRefuelingEnabled = isTruckingOrganizationAdmin && refuelingEnabled;
        return [
            ...(syncRefuelingEnabled ? [checkboxColumn] : []),
            rowIndexColumn,
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Номер",
                accessor: "number",
                filterable: true,
                Cell: ({
                    value,
                    row: {
                        original: { id, isInRefuelingSyncList },
                    },
                }) => (
                    <>
                        <Link to={paths.trucksShow(id)}>{value}</Link>
                        {isInRefuelingSyncList && !refuelingSettings.hideRefuelingPlanning && (
                            <Tooltip overlay="Участвует в планировании АЗС">
                                <i className="icon-uEA36-azs pl-2 text-success align-middle" />
                            </Tooltip>
                        )}
                    </>
                ),
            },
            {
                Header: "Статус",
                accessor: "isActive",
                sortable: true,
                filterable: true,
                filterType: "boolean",
                Filter: (props) => (
                    <BooleanColumnFilter
                        {...props}
                        options={[
                            { label: "Показать все", value: "all" },
                            { label: "Активные", value: "true" },
                            { label: "Неактивные", value: "false" },
                        ]}
                    />
                ),
                Cell: ({ value, row: { original } }) => (
                    <BookItemStatus
                        callback={() => this.props.mergeParams({})}
                        modal={this.props.modal}
                        title={`Изменения статуса грузовика ${original.number}`}
                        initialData={{ carId: original.id }}
                        isActive={value}
                        resourceInactivityInfo={original.resourceInactivityInfo}
                        setResourceActivity={setResourceActivity}
                        canChangeStatus={!policy("user", { user: { roles: this.props.roles } }).isOnlyExtendedViewer()}
                    />
                ),
            },
            {
                Header: "Тип",
                accessor: "typeId",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={this.props.enums.carType} />,
                Cell: ({ value }) => enumLabel(this.props.enums.carType, value, "Не указано"),
                CSVExport: ({ value }) => enumLabel(this.props.enums.carType, value, "Не указано"),
            },
            {
                Header: "Марка",
                accessor: "brandTypeId",
                filterable: true,
                filterType: "enum",
                Filter: (props) => <EnumColumnFilter {...props} filterEnum={this.props.enums.carBrandType} />,
                Cell: ({ value }) => enumLabel(this.props.enums.carBrandType, value, "Не указано"),
                CSVExport: ({ value }) => enumLabel(this.props.enums.carBrandType, value, "Не указано"),
            },
            {
                Header: "Допуски",
                accessor: "accessPermitIds",
                sortable: false,
                filterable: true,
                filterType: "arrayContains",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: (props) =>
                                accessPermitsList({
                                    filter: { relationType: accessPermitRelationTypesEnum.Car },
                                    ...props,
                                }),
                            inputProps: { name: "accessPermitIds" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => <AccessPermitsList ids={value} items={this.state.accessPermits} />,
            },
            {
                Header: "Логист",
                accessor: "logistFullNameOrEmail",
                id: "logistId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: logistsList,
                            inputProps: { name: "logistId" },
                            propName: "fullNameOrEmail",
                        }}
                    />
                ),
            },
            {
                Header: "Механик",
                accessor: "mechanic",
                id: "mechanicId",
                filterable: true,
                filterType: "inArray",
                isMulti: true,
                sortable: false,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: mechanicsList,
                            inputProps: { name: "mechanic" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            {
                Header: "Колонный",
                accessor: "columnDispatcher",
                id: "columnDispatcherId",
                filterable: true,
                filterType: "inArray",
                isMulti: true,
                sortable: false,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            isMulti: true,
                            url: columnDispatchersList,
                            inputProps: { name: "columnDispatcher" },
                            propName: "name",
                            components: { ValueContainer },
                            ...defaultMultipleSelectProps,
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            {
                Header: "Колонна",
                accessor: "transportColumn",
                id: "transportColumn/id",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: transportColumnsList,
                            inputProps: { name: "transportColumnId" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({ value }) => (value ? value.name : ""),
                CSVExport: ({ value }) => (value ? value.name : ""),
            },
            { Header: "Трекер", accessor: "trackerDeviceNumber", filterable: true },
            {
                Header: "Дата привязки трекера",
                accessor: "firstFixedAt",
                filterable: false,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
                CSVExport: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            {
                Header: "Дата последних координат",
                accessor: "lastFixedAt",
                filterable: true,
                filterType: "date",
                Filter: DateColumnFilter,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
                CSVExport: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
            ...(refuelingEnabled ? refuelingInfoColumns : []),
            isDeletedColumn(this.restore, this.props.canEditBooks),
            {
                Header: "Дата удаления",
                accessor: "deletedAt",
                // TODO: need set sortable: true after implementation on the backend
                sortable: false,
                filterable: true,
                filterType: "date",
                Filter: (props) => <DateColumnFilter {...props} libOptions={{ popperPlacement: "right-start" }} />,
                Cell: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
                CSVExport: ({ value }) => dateTimeToView(value, "DD.MM.YYYY HH:mm:ss"),
            },
        ];
    }

    componentDidMount() {
        getInfoList({ preparedFilter: { relationType: accessPermitRelationTypesEnum.Car } }).then((res) =>
            this.setState({ accessPermits: res?.data, accessLoaded: true }),
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.refuelingSettings?.hideRefuelingPlanning !==
                this.props.refuelingSettings?.hideRefuelingPlanning ||
            prevProps.canEditBooks !== this.props.canEditBooks ||
            prevState.accessLoaded !== this.state.accessLoaded
        ) {
            this.setState({ tableColumns: this.tableColumns });
        }
    }

    renderActions = () => {
        const { mergeParams } = this.props;
        const actions = [{ value: "Обновить", cname: "refresh", active: true, action: mergeParams, params: {} }];
        if (this.state.selectedItems.some((item) => item.isInRefuelingSyncList)) {
            actions.unshift({
                value: "Удалить машины из списка синхронизации заправок",
                cname: "delete",
                active: true,
                action: this.removeItemsFromSyncList,
                params: {},
            });
        }
        if (this.state.selectedItems.some((item) => !item.isInRefuelingSyncList)) {
            actions.unshift({
                value: "Добавить машины в список синхронизации заправок",
                cname: "plus",
                active: true,
                action: this.addItemsToSyncList,
                params: {},
            });
        }
        return actions.map((item, index) => <InlineBtn key={index} {...item} />);
    };

    addItemsToSyncList = () => {
        this.props.modal({
            name: "confirm",
            title: "Добавить машины в список синхронизации заправок?",
            buttons: { confirm: "Да", cancel: "Нет" },
            onSuccessAction: () => {
                this.setState({ selectedItems: [] });
                this.props.mergeParams({});
            },
            action: () =>
                addCarsToRefuelingSyncList({
                    carIds: this.state.selectedItems.reduce(
                        (acc, item) => (!item.isInRefuelingSyncList ? acc.concat(item.id) : acc),
                        [],
                    ),
                }),
        });
    };

    removeItemsFromSyncList = () => {
        this.props.modal({
            name: "confirm",
            title: "Удалить машины из списка синхронизации заправок?",
            buttons: { confirm: "Да", cancel: "Нет" },
            onSuccessAction: () => {
                this.setState({ selectedItems: [] });
                this.props.mergeParams({});
            },
            action: () =>
                removeCarsFromRefuelingSyncList({
                    carIds: this.state.selectedItems.reduce(
                        (acc, item) => (item.isInRefuelingSyncList ? acc.concat(item.id) : acc),
                        [],
                    ),
                }),
        });
    };

    renderHead = () => {
        const { canCreateBooks } = this.props;
        return (
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">
                    {this.props.title}
                    {this.renderCounter()}
                </div>
                <div className="d-flex align-items-center">
                    {canCreateBooks && this.renderCreateButton()}
                    {this.renderMoreBtn()}
                </div>
            </div>
        );
    };

    renderMoreBtn = () => {
        const { roles, refuelingSettings } = this.props;
        const { availableFeatureModules } = this.props.profile;
        const syncRefuelingEnabled =
            roles.isTruckingOrganizationAdmin &&
            availableFeatureModules.includes(featureModuleTypesEnum.RefuelingPlanning) &&
            !refuelingSettings.hideRefuelingPlanning;
        const btns = [];
        if (policy("user", { user: { roles } }).isTruckingOrganizationAdmin()) {
            btns.push({
                text: "Добавить машины к логисту",
                action: () => this.props.history.push(paths.trucksAddLogist),
            });
        }
        if (syncRefuelingEnabled) {
            btns.push(
                {
                    text: "Добавление всех машин в список синхронизации заправок",
                    action: () =>
                        this.props.modal({
                            name: "confirm",
                            title: "Добавить все машины в список синхронизации заправок?",
                            buttons: { confirm: "Да", cancel: "Нет" },
                            onSuccessAction: () => {
                                this.setState({ selectedItems: [] });
                                this.props.mergeParams({});
                            },
                            action: addAllCarsToRefuelingSyncList,
                        }),
                },
                {
                    text: "Удаление всех машин из списка синхронизации заправок",
                    action: () =>
                        this.props.modal({
                            name: "confirm",
                            title: "Удалить все машины из списка синхронизации заправок?",
                            buttons: { confirm: "Да", cancel: "Нет" },
                            onSuccessAction: () => {
                                this.setState({ selectedItems: [] });
                                this.props.mergeParams({});
                            },
                            action: removeAllCarsFromRefuelingSyncList,
                        }),
                },
            );
        }
        if (!btns.length) {
            return null;
        }

        if (btns.length === 1) {
            return (
                <div className="btn btn-sm btn-secondary ml-2" onClick={btns[0].action}>
                    {btns[0].text}
                </div>
            );
        }

        const btnProps = {
            text: "Еще",
            cname: "ml-2 btn btn-sm btn-secondary",
            align: "end",
            wrapCname: "b-timeline-point__settings--wrap",
            items: btns,
        };
        return <DropdownBtn {...btnProps} />;
    };

    renderCreateButton = () => {
        const { modal, canEditBooks, roles, profile, refuelingSettings } = this.props;
        const action = () =>
            modal({
                name: "createTruck",
                title: "Создание машины",
                windowMod: "wide",
                canEditBooks,
                roles,
                profile,
                refuelingSettings,
                mergeParams: this.props.mergeParams,
            });
        return (
            <a className="btn btn-brand btn-sm" onClick={action}>
                Создать машину
            </a>
        );
    };

    renderListComponent = () => (
        <SharedReactTable
            {...this.props}
            columns={this.state.tableColumns}
            filtered={{ isDeleted: { filterType: "boolean", id: "isDeleted", value: "false" } }}
        />
    );
}

export default tableContainer(TrucksList, "trucks");
